import { AlertDialog as BaseAlertDialog } from "@base-ui/react/alert-dialog";
import clsx from "clsx";
import React, { forwardRef } from "react";
import { IconClose } from "../icons";
import {
  alertDialogBodyClass,
  alertDialogContentClass,
  alertDialogFooterClass,
  alertDialogHeaderClass,
  alertDialogOverlayClass,
  alertDialogTitlesClass,
} from "./styles.css";

type AlertDialogProps = Omit<React.HTMLAttributes<HTMLDivElement>, "open"> & {
  open?: boolean;
  toggle?: () => void;
  overlayStyles?: React.CSSProperties;
};

const AlertDialogRoot = forwardRef(function AlertDialogRoot(
  {
    open,
    toggle,
    overlayStyles,
    children,
    className,
    ...props
  }: AlertDialogProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const modifiedChildren = React.Children.map(
    children as React.ReactNode,
    (child: React.ReactNode) => {
      if (
        React.isValidElement(child) &&
        (child.props as Record<string, unknown>).hasClose
      ) {
        return React.cloneElement(
          child as React.ReactElement<{ toggle?: () => void }>,
          { toggle },
        );
      }
      return child;
    },
  );

  return (
    <BaseAlertDialog.Root
      open={open}
      onOpenChange={(isOpen, eventDetails) => {
        if (!isOpen && eventDetails.reason !== "escape-key") toggle?.();
      }}
    >
      <BaseAlertDialog.Portal>
        <BaseAlertDialog.Backdrop
          className={alertDialogOverlayClass}
          style={overlayStyles}
          data-slot="alert-dialog-overlay"
        >
          <BaseAlertDialog.Popup
            className={clsx(alertDialogContentClass, className)}
            ref={ref}
            data-slot="alert-dialog-content"
            {...props}
          >
            {modifiedChildren}
          </BaseAlertDialog.Popup>
        </BaseAlertDialog.Backdrop>
      </BaseAlertDialog.Portal>
    </BaseAlertDialog.Root>
  );
});

interface AlertDialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  toggle?: () => void;
  hasClose?: boolean;
}

const Header = ({
  children,
  hasClose,
  toggle,
  className,
  ...props
}: AlertDialogHeaderProps) => {
  return (
    <div
      className={clsx(alertDialogHeaderClass, className)}
      data-slot="alert-dialog-header"
      {...props}
    >
      <div className={alertDialogTitlesClass} data-slot="alert-dialog-titles">
        {children}
      </div>
      {hasClose && <IconClose onClick={() => toggle?.()} />}
    </div>
  );
};

interface AlertDialogBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

const Body = ({ children, className, ...props }: AlertDialogBodyProps) => {
  return (
    <div
      className={clsx(alertDialogBodyClass, className)}
      data-slot="alert-dialog-body"
      {...props}
    >
      {children}
    </div>
  );
};

interface AlertDialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const Footer = ({ children, className, ...props }: AlertDialogFooterProps) => {
  return (
    <div
      className={clsx(alertDialogFooterClass, className)}
      data-slot="alert-dialog-footer"
      {...props}
    >
      {children}
    </div>
  );
};

const AlertDialogComponent = Object.assign(AlertDialogRoot, {
  Header,
  Body,
  Footer,
});

export { AlertDialogComponent as AlertDialog };

AlertDialogRoot.displayName = "AlertDialogRoot";
